import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Search, ArrowRight, Compass, RefreshCw, Building2 } from 'lucide-react';
import { api } from '../services/api';
import { useLocation as useLocationContext } from '../context/LocationContext';
import SEOHead from '../components/SEOHead';
import { getUserChosenMunicipio } from '../services/userChosenMunicipio';


export default function MunicipioLanding() {
  const navigate = useNavigate();
  const { location, requestLocation, loading: locationLoading } = useLocationContext();
  
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [nearestMunicipio, setNearestMunicipio] = useState(null);
  const [findingNearest, setFindingNearest] = useState(false);

  // "Trava de município": se o usuário ainda tem um município escolhido,
  // ele não deve ver a página de seleção — volta para o município.
  // A única saída é o × no topo (que limpa a escolha antes de navegar).
  useEffect(() => {
    const chosen = getUserChosenMunicipio();
    if (chosen?.slug) {
      navigate(`/${chosen.slug}`, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    fetchMunicipios();
  }, []);

  // When location data changes, try to find nearest municipality
  useEffect(() => {
    if (location?.latitude && location?.longitude) {
      findNearestMunicipio(location.latitude, location.longitude);
    }
  }, [location]);

  const fetchMunicipios = async () => {
    try {
      setLoading(true);
      const response = await api.get('/municipios');
      setMunicipios(response.data || []);
    } catch (error) {
      console.error('Error fetching municipalities:', error);
    } finally {
      setLoading(false);
    }
  };

  const findNearestMunicipio = async (lat, lng) => {
    try {
      setFindingNearest(true);
      const response = await api.get('/municipios/nearest', {
        params: { lat, lng }
      });
      if (response.data) {
        setNearestMunicipio(response.data);
      }
    } catch (error) {
      console.error('Error finding nearest municipality:', error);
    } finally {
      setFindingNearest(false);
    }
  };

  const handleSelectMunicipio = (slug) => {
    navigate(`/${slug}`);
  };

  const handleUseLocation = () => {
    // Request location again
    requestLocation();
  };

  const filteredMunicipios = municipios.filter(m => 
    (m.nome || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (m.uf || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // If we found a nearest municipality, show option to go directly
  const showNearestSuggestion = nearestMunicipio && !findingNearest;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#56007F]/5 to-[#FFA52A]/5 pb-20">
      <SEOHead
        title="Encontre Negócios Locais na sua Região"
        description="Descubra os melhores negócios, serviços e profissionais perto de você. Conecte-se com empreendedores locais no Empreende Comunidade."
        canonical="/"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#56007F] to-[#7B2FA8] text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope' }} data-testid="landing-title">
            Empreende Comunidade
          </h1>
          <p className="text-xl text-white/90 mb-8">
            Conectando você aos melhores negócios da sua região
          </p>
          
          {/* Location suggestion */}
          {showNearestSuggestion ? (
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 max-w-lg mx-auto mb-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <MapPin className="w-5 h-5 text-[#FFA52A]" />
                <span className="text-white/90">Parece que você está perto de:</span>
              </div>
              <button
                onClick={() => handleSelectMunicipio(nearestMunicipio.slug)}
                className="w-full bg-[#FFA52A] hover:bg-[#e89420] text-white py-4 px-6 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 transition-colors"
                data-testid="go-to-nearest"
              >
                <Building2 className="w-6 h-6" />
                Ir para {nearestMunicipio.nome}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleUseLocation}
              className="bg-white/20 hover:bg-white/30 backdrop-blur text-white py-3 px-6 rounded-full font-medium flex items-center gap-2 mx-auto transition-colors"
              data-testid="use-location-btn"
              disabled={findingNearest}
            >
              {findingNearest ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Compass className="w-5 h-5" />
              )}
              Usar minha localização
            </button>
          )}
        </div>
      </div>

      {/* Municipality Selection */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Escolha seu município
          </h2>
          <p className="text-gray-600">
            Selecione a cidade para ver os negócios disponíveis
          </p>
        </div>

        {/* Search */}
        <div className="relative max-w-md mx-auto mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar município..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#56007F]/20 focus:border-[#56007F]"
            data-testid="search-municipio"
          />
        </div>

        {/* Municipality Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#56007F]" />
          </div>
        ) : filteredMunicipios.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchQuery ? 'Nenhum município encontrado' : 'Nenhum município disponível'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {filteredMunicipios.map((municipio) => (
              <button
                key={municipio.id}
                onClick={() => handleSelectMunicipio(municipio.slug)}
                className="bg-white border border-gray-200 rounded-xl p-6 text-left hover:border-[#56007F] hover:shadow-lg transition-all group"
                data-testid={`municipio-card-${municipio.slug}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg text-gray-800 group-hover:text-[#56007F] transition-colors">
                      {municipio.nome}
                    </h3>
                    <p className="text-gray-500 text-sm mt-1">
                      {municipio.uf}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-[#56007F]/10 rounded-full flex items-center justify-center group-hover:bg-[#56007F] transition-colors">
                    <ArrowRight className="w-5 h-5 text-[#56007F] group-hover:text-white transition-colors" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            Você poderá trocar de município a qualquer momento
          </p>
        </div>
      </div>

    </div>
  );
}
